import type {
  RichDocumentDiff,
  RichDocumentEnvelope
} from "@notey-g/rich-content";

export type User = {
  id: string;
  email: string;
  role?: string;
  disabled?: boolean;
  preferredName?: string;
  createdAt?: string;
  updatedAt?: string;
  lastLoginAt?: string | null;
};

export type Invite = {
  email?: string;
  role?: string;
  createdAt?: string;
  expiresAt?: string;
};

export type AccountErasureReceipt = {
  operationId: string;
  state: "requested" | "quiesced" | "local_committed" | "external_failed" | "complete";
  subjectUserId: string | null;
  subjectFingerprint: string;
  workspaceIds: string[];
  targetCounts: Record<string, unknown>;
  cleanupStatus: Record<string, unknown>;
  residueReport: Record<string, unknown> & { zeroResidue?: boolean };
  lastErrorCode: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
};

export type RealtimeDiagnostics = {
  generatedAt?: string;
  storage?: {
    backend?: { primary?: string; supported?: boolean; reason?: string };
    collaborationReads?: { detailPrimary?: string; threadListPrimary?: string; writesPrimary?: string; lastError?: string };
    collaborationWrites?: { primary?: string; supported?: boolean; error?: string };
    supabaseShadow?: { enabled?: boolean; pending?: number; successes?: number; failures?: number; lastError?: string };
  };
  realtime?: {
    eventBus?: { backend?: string };
    totals?: { threadEvents?: number; summaryEvents?: number; allEvents?: number };
  };
};

export type AuthStatus = {
  setupRequired?: boolean;
  user?: User | null;
};

export type RichContentCapabilities = {
  format: "notey.content-capabilities";
  contractVersion: number;
  appVersion: string;
  schema: {
    name: string;
    version: number;
    generation: string;
    hash: string;
  };
  document: {
    limits: {
      maxBytes: number;
      maxNodes: number;
      maxDepth: number;
      maxTextLength: number;
      maxAttributeLength: number;
    };
    nodes: string[];
    marks: string[];
    addressableNodes: string[];
  };
  editor: {
    format: string;
    formatVersion: number;
    bundleHash: string;
    bridgeProtocol: { min: number; max: number };
  };
  sync: {
    engine: "yjs";
    updateMaxBytes: number;
    durableReceipts: boolean;
    orderedSequences: boolean;
    snapshotCompaction: boolean;
    transports: { rest: boolean; webSocket: boolean };
    transportWrites: { rest: boolean; webSocket: boolean };
  };
  availability: {
    readable: boolean;
    writable: boolean;
    reason: string | null;
    legacyMarkdownReadable: boolean;
    legacyMarkdownWritable: boolean;
  };
  minimumWritableClients: {
    web: string | null;
    ios: string | null;
    android: string | null;
  };
};

export type RecentItem = {
  id: string;
  kind?: string;
  type?: string;
  title?: string;
  pinned?: boolean;
  rev?: number;
  unreadCount?: number;
  updatedAt?: string;
};

export type NoteMetadataPatch = {
  title?: string;
  tags?: string[];
  pinned?: boolean;
};

export async function buildNoteMetadataUpdate(
  noteId: string,
  expectedRevision: number,
  patch: NoteMetadataPatch
) {
  const id = String(noteId || "").trim();
  if (!id || !Number.isSafeInteger(expectedRevision) || expectedRevision < 1) {
    throw new Error("Metadata updates require an exact note revision.");
  }
  const keys = Object.keys(patch);
  if (
    !keys.length
    || keys.some(key => !["title", "tags", "pinned"].includes(key))
    || (patch.title !== undefined && typeof patch.title !== "string")
    || (patch.tags !== undefined && !Array.isArray(patch.tags))
    || (patch.pinned !== undefined && typeof patch.pinned !== "boolean")
  ) {
    throw new Error("Metadata updates may contain only title, tags, or pinned.");
  }
  const canonicalPatch: NoteMetadataPatch = {
    ...(patch.title !== undefined ? { title: patch.title } : {}),
    ...(patch.tags !== undefined ? { tags: patch.tags.map(tag => String(tag)) } : {}),
    ...(patch.pinned !== undefined ? { pinned: patch.pinned } : {})
  };
  const encoded = new TextEncoder().encode(JSON.stringify({
    contractVersion: 1,
    operation: "note.update",
    noteId: id,
    expectedRevision,
    patch: canonicalPatch
  }));
  const digest = await crypto.subtle.digest("SHA-256", encoded);
  const hash = [...new Uint8Array(digest)]
    .map(value => value.toString(16).padStart(2, "0"))
    .join("");
  return Object.freeze({
    ...canonicalPatch,
    baseRev: expectedRevision,
    idempotencyKey: `webmeta:${hash}`
  });
}

export function guardedRecentPinMutation(item: RecentItem, enabled: boolean) {
  const kind = item.kind || item.type || "note";
  const revision = Number(item.rev);
  if (
    !enabled
    || kind === "thread"
    || kind === "chat"
    || !Number.isSafeInteger(revision)
    || revision < 1
  ) {
    return null;
  }
  return {
    expectedRevision: revision,
    patch: { pinned: !Boolean(item.pinned) } satisfies NoteMetadataPatch
  };
}

export type SearchNoteResult = {
  id: string;
  noteId?: string;
  blockId?: string | null;
  deepLink?: string;
  folderId?: string;
  path?: string;
  title?: string;
  tags?: string[];
  updatedAt?: string;
  snippet?: string;
  score?: number;
};

export type RichSearchLegacyFallback = "none" | "when-empty" | "only";

export type RichSearchResult = {
  authority: "rich-projection" | "legacy-markdown";
  freshness: "projection-fresh" | "not-rich-materialized";
  noteId: string;
  blockId: string | null;
  kind: string | null;
  folderId: string;
  title: string;
  updatedAt: string | null;
  score: number;
  heading: {
    blockId: string;
    text: string;
  } | null;
  snippet: string;
  contentHash: string | null;
  contentRevision: number;
  projectionRevision: number;
  migrationRequired: boolean;
};

export type RichSearchResponse = {
  format: "notey.rich-search-results";
  contractVersion: 1;
  query: string;
  results: RichSearchResult[];
  page: {
    limit: number;
    count: number;
    hasMore: boolean;
    afterCursor: string | null;
  };
  source: "projection-fresh-rich" | "legacy-unmaterialized";
  fallback: {
    policy: RichSearchLegacyFallback;
    used: boolean;
    reason:
      | "explicit_legacy_only"
      | "legacy_page_continuation"
      | "no_fresh_rich_matches"
      | null;
  };
};

export type RichSearchOptions = {
  limit?: number;
  after?: string;
  legacyFallback?: RichSearchLegacyFallback;
};

export type RichSmartFolderField =
  | "note.title"
  | "note.folderId"
  | "note.createdAt"
  | "note.updatedAt"
  | "note.pinned"
  | "note.unlinked"
  | "tag.value"
  | "action.status"
  | "action.dueDate"
  | "action.remindAt"
  | "action.priority"
  | "action.project"
  | "action.context"
  | "asset.kind"
  | "link.targetNoteId"
  | "link.unresolvedTitle"
  | "search.keyword";

export type RichSmartFolderOperator =
  | "eq"
  | "neq"
  | "in"
  | "not_in"
  | "contains"
  | "exists"
  | "before"
  | "after"
  | "between"
  | "gt"
  | "gte"
  | "lt"
  | "lte";

export type RichSmartFolderSortField =
  | "note.title"
  | "note.createdAt"
  | "note.updatedAt"
  | "note.pinned"
  | "action.dueDate"
  | "action.remindAt";

export type RichSmartFolderSort = {
  field: RichSmartFolderSortField;
  direction: "asc" | "desc";
};

export type RichSmartFolderRule =
  | {
      field: RichSmartFolderField;
      op: RichSmartFolderOperator;
      value?: unknown;
    }
  | { all: RichSmartFolderRule[] }
  | { any: RichSmartFolderRule[] }
  | { not: RichSmartFolderRule };

export type RichSmartFolderDefinition = {
  version: 1;
  all?: RichSmartFolderRule[];
  any?: RichSmartFolderRule[];
  not?: RichSmartFolderRule;
  sort?: RichSmartFolderSort[];
};

export type RichSmartFolderPresentation = Record<string, unknown> & {
  parentFolderId?: string;
  icon?: string;
  layout?: string;
};

export type RichSmartFolder = {
  id: string;
  name: string;
  description?: string;
  definitionVersion?: number;
  query: RichSmartFolderDefinition;
  sort?: RichSmartFolderSort[];
  presentation?: RichSmartFolderPresentation;
  readOnly?: boolean;
  rev?: number;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
};

export type RichSmartFolderListResponse = {
  format: "notey.rich-smart-folder-list";
  contractVersion: 1;
  smartFolders: RichSmartFolder[];
  page: {
    limit: number;
    count: number;
    hasMore: boolean;
    afterCursor: string | null;
  };
};

export type RichSmartFolderResponse = {
  format: "notey.rich-smart-folder";
  contractVersion: 1;
  smartFolder: RichSmartFolder;
};

export type RichSmartFolderResult = {
  id: string;
  folderId: string;
  title: string;
  pinned: boolean;
  pinnedAt: string | null;
  createdAt: string;
  updatedAt: string;
  legacyRevision: number;
  contentRevision: number;
  projectionRevision: number;
  contentHash: string;
};

export type RichSmartFolderResultsResponse = {
  format: "notey.rich-smart-folder-results";
  contractVersion: 1;
  smartFolder: RichSmartFolder;
  results: RichSmartFolderResult[];
  page: {
    limit: number;
    count: number;
    hasMore: boolean;
    afterCursor: string | null;
  };
  execution: {
    engineVersion: number;
    ruleCount: number;
    maximumDepth: number;
    elapsedMs: number;
    temporalAnchor: string;
    timeZone: string;
    projectionPolicy: "fresh_only";
  };
};

export type RichSmartFolderCreateInput = {
  name: string;
  query: RichSmartFolderDefinition;
  sort?: RichSmartFolderSort[];
  presentation?: RichSmartFolderPresentation;
};

export type RichSmartFolderUpdateInput = Partial<
  Omit<RichSmartFolderCreateInput, "query">
> & {
  expectedRevision: number;
  query?: RichSmartFolderDefinition;
};

export type NoteSummary = {
  id: string;
  folderId?: string;
  title?: string;
  pinned?: boolean;
  pinnedAt?: string | null;
  updatedAt?: string;
  rev?: number;
};

export type Note = NoteSummary & {
  body?: string;
  tags?: string[];
  aiManaged?: boolean;
  createdAt?: string;
};

export type RichNoteContent = {
  format: "notey.rich-note-content";
  contractVersion: number;
  authority: "legacy-projection" | "rich-mirror";
  /**
   * Whether the legacy Markdown body remains writable or the durable rich
   * journal has taken ownership. A read-mirror alone never implies a lock.
   */
  bodyAuthority?: "legacy-markdown" | "rich-locked";
  writable: boolean;
  note: Note;
  content: {
    document: RichDocumentEnvelope;
    schema: string;
    schemaVersion: number;
    schemaHash: string | null;
    generation: string;
    contentHash: string;
    contentRevision: number;
    projectionRevision: number;
    plainText: string;
    compatibilityMarkdown: string;
    compatibilityWarnings: unknown[];
    sync: {
      engine: string;
      generation?: string;
      sequence: number;
      activeSnapshotId: string | null;
    };
    createdAt?: string;
    updatedAt?: string;
  };
  migration?: {
    required: boolean;
    staleMirror?: boolean;
    staleMirrorRevision?: number | null;
    adapterVersion: string;
    sourceHash: string;
    warningCount: number;
    loss: {
      exactSourceBodyAvailable: boolean;
      compatibilityBodyChanged: boolean | null;
      importWarningCount: number;
      markdownProjectionWarningCount: number;
    };
  };
};

export type RichNoteVersionSummary = {
  id: string;
  noteId: string;
  contentRevision: number;
  contentHash: string;
  schema: string;
  schemaVersion: number;
  schemaHash: string;
  generation: string;
  mutationId: string;
  actorId: string | null;
  deviceId: string | null;
  reason: string;
  restoreOfVersionId: string | null;
  createdAt: string;
  compatibilityWarningCount: number;
};

export type RichNoteHistory = {
  format: "notey.rich-history";
  contractVersion: 1;
  noteId: string;
  versions: RichNoteVersionSummary[];
};

export type RichNoteSnapshot = {
  source: "current" | "version";
  versionId: string | null;
  noteId: string;
  contentRevision: number;
  contentHash: string;
  schema: string;
  schemaVersion: number;
  schemaHash: string;
  generation: string;
  document: RichDocumentEnvelope;
  plainText: string;
  compatibilityMarkdown: string;
  compatibilityWarnings: unknown[];
  mutationId: string | null;
  actorId: string | null;
  deviceId: string | null;
  reason: string | null;
  restoreOfVersionId: string | null;
  createdAt: string;
  updatedAt: string;
  sync: {
    engine: string;
    generation?: string;
    sequence: number;
    activeSnapshotId: string | null;
  } | null;
};

export type RichNoteHistoryDiff = {
  format: "notey.rich-history-diff";
  contractVersion: 1;
  noteId: string;
  before: {
    versionId: string;
    contentRevision: number;
    contentHash: string;
  };
  after: {
    versionId: string | null;
    contentRevision: number;
    contentHash: string;
  };
  diff: RichDocumentDiff;
};

export type RichNoteHistoryRestore = {
  restore: {
    format: "notey.rich-history-restore";
    contractVersion: 1;
    noteId: string;
    versionId: string;
    operationId: string;
    status: "restored" | "noop";
    contentRevision: number;
    contentHash: string;
  };
  receipt: RichSyncReceipt | null;
};

export type RichNoteExportFormat = "html" | "markdown" | "plain-text";

export type RichNoteExport = {
  format: `notey.rich-export.${RichNoteExportFormat}`;
  contractVersion: 1;
  noteId: string;
  versionId: string | null;
  contentRevision: number;
  contentHash: string;
  mediaType: string;
  extension: "html" | "md" | "txt";
  filename: string;
  content: string;
  bytes: number;
  warnings: unknown[];
};

export type RichNoteExportOptions = {
  format?: RichNoteExportFormat;
  versionId?: string;
};

export const RICH_IMAGE_UPLOAD_MAX_BYTES = 8 * 1024 * 1024;
export const RICH_IMAGE_UPLOAD_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "image/avif"
] as const;
export const RICH_IMAGE_UPLOAD_ACCEPT = RICH_IMAGE_UPLOAD_MIME_TYPES.join(",");

export type RichImageUploadMimeType =
  typeof RICH_IMAGE_UPLOAD_MIME_TYPES[number];

export type RichImageAsset = {
  assetId: string;
  url: string;
  metadata: {
    mimeType: RichImageUploadMimeType;
    byteSize: number;
    width: number;
    height: number;
    contentHash: `sha256:${string}`;
    originalFilename: string | null;
    immutable: true;
    createdAt: string;
  };
};

export type RichSyncMetadata = {
  noteId: string;
  documentId: string;
  engine: "yjs";
  schema: string;
  schemaVersion: number;
  schemaHash: string;
  generation: string;
  syncGeneration: string;
};

export type RichSyncReceipt = {
  format: "notey.rich-sync-receipt";
  contractVersion: number;
  noteId: string;
  syncGeneration: string;
  updateId: string;
  sequence: number;
  payloadBytes: number;
  checksum: string;
  durable: boolean;
  duplicate: boolean;
  durableAt: string | null;
};

export type RichSyncBootstrap = {
  format: "notey.rich-sync-bootstrap";
  contractVersion: number;
  metadata: RichSyncMetadata;
  document: {
    contentHash: string;
    contentRevision: number;
    projectionRevision: number;
  };
  cursor: {
    requestedSequence: number;
    activeSnapshotSequence: number;
    lastSequence: number;
    resetToSnapshot: boolean;
  };
  snapshot: {
    snapshotId: string;
    throughSequence: number;
    payload: string;
    payloadBytes: number;
    checksum: string;
    documentHash: string | null;
    materializedRevision: number | null;
  } | null;
  updates: Array<{
    sequence: number;
    updateId: string;
    payload: string;
    payloadBytes: number;
    checksum: string;
    durableAt: string | null;
  }>;
};

export type RichSyncUpdateInput = {
  updateId: string;
  payload: string;
  checksum?: string;
  idempotencyKey?: string;
  baseSequence: number;
  observedSequence: number;
  client: {
    schema: string;
    schemaVersion: number;
    generation: string;
    schemaHash: string;
    syncGeneration: string;
  };
};

export type RichSyncToken = {
  token: string;
  expiresAt: string;
  room: string;
  permission: "read" | "write";
  schemaHash: string;
  syncGeneration: string;
  endpoint: string;
  providerMajor: number;
  requestedPermission: "read" | "write";
  downgraded: boolean;
};

export type FolderNode = {
  id: string;
  name: string;
  parentId?: string;
  path?: string;
  rev?: number;
  folders: FolderNode[];
  notes: NoteSummary[];
};

export type TreeResponse = {
  tree: {
    folders: FolderNode[];
    notes: NoteSummary[];
  };
};

export type SearchResponse = {
  authority?: "rich-document" | "legacy-markdown";
  projectionFresh?: boolean;
  stableBlockTargets?: boolean;
  fallbackReason?: string | null;
  results: SearchNoteResult[];
};

export type ChatThread = {
  id: string;
  title?: string;
  contextNoteId?: string | null;
  model?: string;
  summary?: string;
  pinned?: boolean;
  archivedAt?: string | null;
  isSystem?: boolean;
  systemKey?: string;
  lastReadAt?: string;
  unreadCount?: number;
  createdAt?: string;
  updatedAt?: string;
  lastMessageAt?: string;
};

export type ChatMessage = {
  id: string;
  threadId?: string;
  role: "user" | "assistant" | "system" | string;
  content?: string;
  jobId?: string;
  status?: string;
  metadata?: Record<string, unknown>;
  createdAt?: string;
};

export type ChatJob = {
  id: string;
  status?: string;
  title?: string;
  contextNoteId?: string | null;
  threadId?: string;
  messages?: Array<Record<string, unknown>>;
  toolCalls?: ChatToolCall[];
  result?: string;
  progress?: Record<string, unknown>;
  error?: string;
  costTokensIn?: number;
  costTokensOut?: number;
  costUsd?: number;
  costDisplay?: string;
  tokensPerSecond?: number | null;
  model?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type SourceDocument = {
  id: string;
  name: string;
  description: string;
  mimeType: string;
  byteSize: number;
  status: "uploaded" | "processing" | "ready" | "failed" | "needs_ocr";
  error: string | null;
  pageCount: number;
  chunkCount: number;
  embeddingStatus: "pending" | "ready" | "no_key" | "failed";
  embeddingTokens: number;
  embeddingCostUsd: number;
  createdAt: string;
  updatedAt: string;
};

export type SourceChunk = {
  id: string;
  sourceId: string;
  sourceName: string;
  ordinal: number;
  pageNumber: number | null;
  heading: string;
  content: string;
  citation?: string;
  deepLink?: string;
};

export type ChatToolCall = {
  id?: string;
  name?: string;
  status?: string;
  input?: unknown;
  output?: unknown;
  error?: string;
  approvalId?: string;
  startedAt?: string;
  completedAt?: string;
  generationStats?: Record<string, unknown> | null;
  tokensPerSecond?: number | null;
};

export type ChatAttachment = {
  name: string;
  mimeType: string;
  size: number;
  data: string;
};

export type WebPreview = {
  title: string;
  markdown: string;
  excerpt?: string;
  url: string;
};

export type ChatSelectionContext = {
  noteId?: string | null;
  noteTitle?: string;
  text: string;
};

export type ChatApproval = {
  id: string;
  jobId?: string;
  toolCallId?: string;
  status?: string;
  toolName?: string;
  toolInput?: Record<string, unknown>;
  createdAt?: string;
  resolvedAt?: string | null;
};

export type ActionItem = {
  id: string;
  blockId?: string | null;
  noteId: string;
  noteTitle: string;
  folderId?: string;
  lineIndex: number | null;
  lineNumber: number | null;
  raw?: string;
  title: string;
  completed: boolean;
  checked?: boolean;
  status?: string;
  due?: string;
  dueDate?: string | null;
  reminder?: string;
  reminderSpecRaw?: string | null;
  remindAt?: string | null;
  timeZone?: string | null;
  recurrence?: string;
  recurrenceRaw?: string | null;
  priority?: string;
  project?: string;
  context?: string;
  tags?: string[];
  waiting?: boolean;
  waitingFor?: string | null;
  completedAt?: string | null;
  legacyActionIds?: string[];
  mutationActionId?: string | null;
  mutationWritable?: boolean;
  mutationMode?: "rich-semantic" | "legacy-compatibility" | "read-only";
  deepLink?: string;
  updatedAt?: string;
};

export type ActionsResponse = {
  authority: "rich-document" | "legacy-markdown";
  projectionFresh: boolean;
  stableIds: boolean;
  fallbackReason: string | null;
  mutationMode: "rich-semantic" | "legacy-markdown" | "legacy-compatibility" | "mixed" | "read-only";
  actions: ActionItem[];
};

export type SkillRunSnapshot = {
  id?: string;
  status?: string;
  jobId?: string;
  startedAt?: string;
  completedAt?: string;
  updatedAt?: string;
  error?: string;
};

export type SkillTestCase = {
  id: string;
  input?: string;
  expect?: string;
  actual?: string;
  status?: string;
  jobId?: string;
};

export type Skill = {
  id: string;
  title?: string;
  description?: string;
  source?: string;
  sourceLabel?: string;
  favorite?: boolean;
  draft?: boolean;
  model?: string;
  tools?: string[];
  activationPhrases?: string[];
  testCases?: SkillTestCase[];
  tests?: SkillTestCase[];
  testStatus?: string;
  version?: string;
  schedule?: SkillSchedule | null;
  lastRun?: SkillRunSnapshot | Record<string, unknown> | null;
  updatedAt?: string;
};

export type SkillSchedule = {
  enabled?: boolean;
  type?: "daily" | "weekly" | "monthly" | "interval" | "once" | string;
  time?: string;
  on?: string;
  day?: number;
  interval?: string;
};

export type StarterSkill = {
  id: string;
  title: string;
  description?: string;
  activationPhrases?: string[];
  tools?: string[];
  defaultModel?: string;
  suggestedModel?: string;
  installed?: boolean;
};

export type Workflow = {
  id: string;
  title?: string;
  description?: string;
  sourceLabel?: string;
  steps?: Array<Record<string, unknown>>;
  schedule?: Record<string, unknown> | null;
  lastRun?: SkillRunSnapshot | Record<string, unknown> | null;
  updatedAt?: string;
};

export type SkillSections = {
  favorites?: Skill[];
  drafts?: Skill[];
  scheduled?: Skill[];
  recentlyRun?: Skill[];
  installed?: Skill[];
};

export type SkillVersion = {
  id: string;
  rev?: number;
  reason?: string;
  body?: string;
  createdAt?: string;
};

export type ChatThreadDetail = {
  thread: ChatThread;
  messages: ChatMessage[];
  jobs?: ChatJob[];
  approvals?: ChatApproval[];
  page?: {
    limit?: number;
    hasMoreBefore?: boolean;
    beforeCursor?: string;
    loadedCount?: number;
  };
};

export type ChatThreadsResponse = {
  threads: ChatThread[];
  page?: {
    nextCursor?: string;
    afterCursor?: string;
    hasMoreAfter?: boolean;
  };
  unreadTotal?: number;
};

export type ChatSendResponse = {
  thread?: ChatThread;
  userMessage?: ChatMessage;
  assistantMessage?: ChatMessage;
  job?: ChatJob | null;
  transcriptionJobs?: ChatJob[];
};

export type ModelInfo = {
  id: string;
  name?: string;
  provider?: string;
  inputModalities?: string[];
  input_modalities?: string[];
  outputModalities?: string[];
  inMtok?: number;
  outMtok?: number;
  costPerHour?: number | null;
  pricingUnit?: string | null;
  pricing?: Record<string, unknown>;
};

export type FreeRouterStatus = {
  requiresOpenRouterKey?: boolean;
  dailyCap?: number;
  usedToday?: number;
  minuteRemaining?: number;
  nextAvailableAt?: string | null;
  catalogSize?: number;
};

export type RecentResponse = {
  items?: RecentItem[];
  recent?: RecentItem[];
  workspaceMetadataMutations?: {
    enabled: boolean;
    operations: Array<"folder.create" | "folder.update" | "folder.move" | "folder.trash" | "note.create" | "note.update" | "note.move" | "note.trash" | "trash.restore">;
  };
  page?: {
    hasMoreAfter?: boolean;
    afterCursor?: string;
    nextCursor?: string;
    loadedCount?: number;
    limit?: number;
  };
};

export type SettingsResponse = {
  settings: {
    appVersion?: string;
    hasOpenRouterKey?: boolean;
    chatModel?: string | null;
    defaultChatModel?: string;
    defaultResearchModel?: string;
    transcriptionModel?: string;
    researchModel?: string | null;
    startScreen?: string;
    onboardingComplete?: boolean;
    freeModeEnabled?: boolean;
    memoryPreferences?: {
      enabled: boolean;
      learningMode: "review" | "off";
      episodeRetentionDays: number;
      sensitiveDataPolicy: "reject";
    };
    freeRouter?: FreeRouterStatus;
    searchKeys?: {
      hasBraveKey?: boolean;
      hasTavilyKey?: boolean;
      braveFromServer?: boolean;
      tavilyFromServer?: boolean;
    };
    clipToken?: string | null;
  };
};

export type PushSubscriptionSummary = {
  endpoint: string;
  userAgent?: string;
  createdAt?: string;
};

export type TranscriptionProfile = {
  id: string;
  name?: string;
  engine?: string;
  acceleration?: string;
};

export type TranscriptionConnector = {
  id: string;
  displayName?: string;
  status?: string;
  capabilities?: {
    profiles?: TranscriptionProfile[];
  };
  selectedProfileId?: string;
  lastSeenAt?: string;
  registeredAt?: string;
  revokedAt?: string;
  lastError?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type WikiApp = {
  id: string;
  displayName?: string;
  baseUrl?: string;
  status?: string;
  capabilities?: Record<string, unknown>;
  wikis?: Array<Record<string, unknown>>;
  lastValidatedAt?: string;
  lastError?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type McpServer = {
  id: string;
  name?: string;
  baseUrl?: string;
  authType?: string;
  status?: string;
  metadata?: {
    provider?: string;
    presetId?: string;
    oauthRequired?: boolean;
    oauth?: {
      connected?: boolean;
      pending?: boolean;
      expiresAt?: string | null;
      scope?: string;
    };
    toolCache?: {
      cachedAt?: string | null;
      toolCount?: number;
    };
  } & Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
};

export type McpPreset = {
  id: string;
  name?: string;
  provider?: string;
  description?: string;
  services?: string[];
  status?: string;
  authType?: string;
  baseUrl?: string;
  urlRequired?: boolean;
  setupHint?: string;
  installed?: boolean;
  serverId?: string | null;
  connectionStatus?: string;
};

export type AiMemory = {
  id: string;
  title?: string;
  content?: string;
  type?: string;
  scope?: string;
  scopeId?: string | null;
  importance?: number;
  confidence?: number;
  useCount?: number;
  enabled?: boolean;
  archived?: boolean;
  candidate?: boolean;
  sourceJobId?: string;
  sourceThreadId?: string;
  sourceNoteId?: string;
  updatedAt?: string;
};

export type AiMemoryEvidence = {
  id: string;
  memoryNoteId: string;
  sourceKind: string;
  sourceId: string;
  relation: string;
  excerpt?: string;
  observedAt: string;
  extractorVersion?: string;
  createdAt: string;
};

export type AiMemoryEpisode = {
  id: string;
  sourceThreadId?: string | null;
  sourceJobId?: string | null;
  sourceMessageIds?: string[];
  summary: string;
  outcome?: string;
  tools?: string[];
  topics?: string[];
  startedAt?: string | null;
  endedAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type AiLogSummary = {
  id: string;
  title?: string;
  kind?: string;
  preview?: string;
  updatedAt?: string;
};

export type AiLog = AiLogSummary & {
  body?: string;
};

export type TrashItem = {
  id: string;
  type: "note" | "folder";
  title?: string;
  name?: string;
  folderId?: string;
  parentId?: string;
  deletedAt?: string;
  updatedAt?: string;
  rev?: number;
};

export type WorkspacePurgeResult = {
  format: "notey.workspace-purge-result";
  contractVersion: number;
  operationId: string | null;
  state: "complete";
  deletedNotes: number;
  deletedFolders: number;
  reminderActionsRemoved?: number;
  migrationArchivesRemoved?: number;
  assetCandidates?: number;
  resumedOperations: string[];
};

export type NoteShare = {
  id: string;
  url?: string;
  allowPdf?: boolean;
  revokedAt?: string | null;
};

export type NoteVersion = {
  id: string;
  title?: string;
  body?: string;
  reason?: string;
  createdAt?: string;
};

export type NoteLinkPreview = {
  id: string;
  title?: string;
  folderId?: string;
  path?: string;
  snippet?: string;
  score?: number;
  sourceBlockId?: string | null;
  sourceDeepLink?: string;
  targetIdentityStable?: boolean;
  resolutionOrigin?: string | null;
};

export type BacklinksResponse = {
  authority: "rich-document" | "legacy-markdown";
  projectionFresh: boolean;
  stableIds: boolean;
  fallbackReason: string | null;
  backlinks: NoteLinkPreview[];
  ambiguousReferences: NoteLinkPreview[];
  page?: {
    limit?: number;
    hasMoreBacklinks?: boolean;
    hasMoreAmbiguous?: boolean;
  };
};

export type NoteLinkSuggestion = {
  id: string;
  title: string;
  folderId?: string;
  path?: string;
  updatedAt?: string | null;
  duplicateTitleCount: number;
};

export type NoteLinkSuggestionsResponse = {
  authority: "rich-document" | "legacy-markdown";
  projectionFresh: boolean;
  stableIds: boolean;
  fallbackReason: string | null;
  query: string;
  suggestions: NoteLinkSuggestion[];
};

export type OutgoingNoteLink = {
  id?: string | null;
  kind: "wikilink" | "blockLink" | "url";
  label: string;
  rawTarget?: string | null;
  sourceBlockId?: string | null;
  sourceDeepLink?: string;
  targetNoteId?: string | null;
  targetNoteTitle?: string | null;
  targetFolderId?: string;
  targetPath?: string;
  targetBlockId?: string | null;
  targetDeepLink?: string | null;
  resolutionState: "resolved" | "unresolved" | "ambiguous" | "external";
  resolutionReason?: string | null;
  targetIdentityStable: boolean;
  candidates?: {
    count: number;
    truncated: boolean;
    notes: NoteLinkSuggestion[];
  };
};

export type OutgoingLinksResponse = {
  authority: "rich-document" | "legacy-markdown";
  projectionFresh: boolean;
  stableIds: boolean;
  fallbackReason: string | null;
  links: OutgoingNoteLink[];
  page?: {
    limit?: number;
    hasMore?: boolean;
  };
};

export type PushSupportSnapshot = {
  supported: boolean;
  isIos: boolean;
  standalone: boolean;
  needsHomeScreenInstall: boolean;
  permission: NotificationPermission | "unsupported";
};

export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

export type SpeechModel = { id: string; name: string; voices: string[]; costPerCharacter: number };

async function openSpeechStream(body: { input: string; model: string; voice: string; threadId?: string; voiceSessionId?: string }, signal?: AbortSignal) {
  const response = await fetch("/api/voice/speech", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", "X-Notey-Client": `web/${__APP_VERSION__}` },
    body: JSON.stringify(body),
    signal
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new ApiError(String(data.error || `Speech failed (${response.status}).`), response.status, data);
  }
  return response;
}

async function synthesizeSpeech(body: { input: string; model: string; voice: string; threadId?: string; voiceSessionId?: string }, signal?: AbortSignal) {
  const response = await openSpeechStream(body, signal);
  return {
    blob: await response.blob(),
    estimatedCost: Number(response.headers.get("X-Notey-Speech-Estimated-Cost") || 0)
  };
}

async function request<T>(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const options: RequestInit = {
    method,
    credentials: "same-origin",
    headers: {
      "X-Notey-Client": `web/${__APP_VERSION__}`
    },
    signal
  };
  if (body !== undefined) {
    (options.headers as Record<string, string>)["Content-Type"] = "application/json";
    options.body = JSON.stringify(body);
  }
  const response = await fetch(path, options);
  let data: unknown = null;
  try {
    data = await response.json();
  } catch {
    /* non-JSON response */
  }
  if (!response.ok) {
    const message = data && typeof data === "object" && "error" in data
      ? String((data as { error?: unknown }).error)
      : `Request failed (${response.status}).`;
    throw new ApiError(message, response.status, data);
  }
  return data as T;
}

function uploadSourceFile(file: File, onProgress?: (fraction: number) => void): Promise<{ source: SourceDocument; job: ChatJob }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", "/api/sources");
    xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
    xhr.setRequestHeader("X-Source-Name", encodeURIComponent(file.name));
    xhr.setRequestHeader("X-Notey-Client", `web/${__APP_VERSION__}`);
    xhr.upload.onprogress = event => { if (event.lengthComputable) onProgress?.(event.loaded / event.total); };
    xhr.onerror = () => reject(new ApiError("Document upload failed.", 0, null));
    xhr.onload = () => {
      let data: Record<string, unknown> = {};
      try { data = JSON.parse(xhr.responseText) as Record<string, unknown>; } catch { /* Keep the HTTP status. */ }
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new ApiError(String(data.error || `Upload failed (${xhr.status}).`), xhr.status, data));
      } else resolve(data as { source: SourceDocument; job: ChatJob });
    };
    xhr.send(file);
  });
}

export function richNoteAssetUrl(noteId: string, assetId: string) {
  return `/assets/notes/${encodeURIComponent(noteId)}/${encodeURIComponent(assetId)}`;
}

async function uploadRichNoteImage(
  noteId: string,
  file: File,
  { signal }: { signal?: AbortSignal } = {}
): Promise<RichImageAsset> {
  const query = new URLSearchParams({ filename: file.name });
  const response = await fetch(
    `/api/notes/${encodeURIComponent(noteId)}/assets/images?${query.toString()}`,
    {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": file.type
      },
      body: file,
      signal
    }
  );
  let data: unknown = null;
  try {
    data = await response.json();
  } catch {
    /* non-JSON response */
  }
  if (!response.ok) {
    const message = data && typeof data === "object" && "error" in data
      ? String((data as { error?: unknown }).error)
      : `Image upload failed (${response.status}).`;
    throw new ApiError(message, response.status, data);
  }
  if (
    !data
    || typeof data !== "object"
    || !("asset" in data)
  ) {
    throw new ApiError("The image upload response was invalid.", 502, data);
  }
  return (data as { asset: RichImageAsset }).asset;
}

/**
 * Fetches a stored PDF stored for a job and hands it to the browser as a file
 * download. Throws ApiError when the export is missing or expired so the UI
 * can show the server's reason.
 */
async function downloadPdfExportFile(jobId: string, filename: string): Promise<void> {
  const response = await fetch(`/api/jobs/${encodeURIComponent(jobId)}/pdf`, {
    credentials: "same-origin",
    headers: { "X-Notey-Client": `web/${__APP_VERSION__}` }
  });
  if (!response.ok) {
    let data: unknown = null;
    try {
      data = await response.json();
    } catch {
      /* non-JSON response */
    }
    const message = data && typeof data === "object" && "error" in data
      ? String((data as { error?: unknown }).error)
      : `Download failed (${response.status}).`;
    throw new ApiError(message, response.status, data);
  }
  const blob = await response.blob();
  const { savePdfBlob } = await import("../features/notes/pdfDocument");
  savePdfBlob(blob, filename);
}

function uploadBinary<T>(path: string, body: Blob, contentRange: string, onProgress?: (loaded: number) => void): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", path);
    xhr.withCredentials = true;
    xhr.setRequestHeader("Content-Type", "application/octet-stream");
    xhr.setRequestHeader("Content-Range", contentRange);
    xhr.upload.addEventListener("progress", (event) => onProgress?.(event.loaded));
    xhr.addEventListener("error", () => reject(new ApiError("Could not upload the audio file.", 0, null)));
    xhr.addEventListener("abort", () => reject(new ApiError("Audio upload was canceled.", 0, null)));
    xhr.addEventListener("load", () => {
      let data: unknown = null;
      try {
        data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        /* non-JSON response */
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        const message = data && typeof data === "object" && "error" in data
          ? String((data as { error?: unknown }).error)
          : `Request failed (${xhr.status}).`;
        reject(new ApiError(message, xhr.status, data));
        return;
      }
      resolve(data as T);
    });
    xhr.send(body);
  });
}

async function uploadTranscriptionFile(jobId: string, file: File, onProgress?: (percent: number) => void) {
  const chunkBytes = 8 * 1024 * 1024;
  let offset = 0;
  let failures = 0;
  while (offset < file.size) {
    const end = Math.min(file.size, offset + chunkBytes);
    const chunk = file.slice(offset, end);
    try {
      const result = await uploadBinary<{ uploadedBytes: number; totalBytes: number; job: ChatJob }>(
        `/api/transcriptions/${encodeURIComponent(jobId)}/upload`,
        chunk,
        `bytes ${offset}-${end - 1}/${file.size}`,
        (loaded) => onProgress?.(Math.max(0, Math.min(100, Math.round(((offset + loaded) / file.size) * 100))))
      );
      offset = Number(result.uploadedBytes || end);
      failures = 0;
      onProgress?.(Math.max(0, Math.min(100, Math.round((offset / file.size) * 100))));
    } catch (error) {
      const expectedOffset = error instanceof ApiError && error.data && typeof error.data === "object" && "expectedOffset" in error.data
        ? Number((error.data as { expectedOffset?: unknown }).expectedOffset)
        : NaN;
      if (error instanceof ApiError && error.status === 409 && Number.isFinite(expectedOffset) && expectedOffset > offset && expectedOffset <= file.size) {
        offset = expectedOffset;
        continue;
      }
      failures += 1;
      if (failures >= 3 || (error instanceof ApiError && error.status >= 400 && error.status < 500)) throw error;
      await new Promise((resolve) => window.setTimeout(resolve, failures * 300));
    }
  }
}

async function streamRequest(
  method: string,
  path: string,
  body: unknown,
  { signal, onEvent, onHeaders }: { signal?: AbortSignal; onEvent?: (eventName: string, data: unknown) => void; onHeaders?: (response: Response) => void } = {}
) {
  if (typeof ReadableStream === "undefined") {
    throw new ApiError("Streaming is not supported by this browser.", 0, null);
  }
  const options: RequestInit = {
    method,
    credentials: "same-origin",
    headers: {
      "X-Notey-Client": `web/${__APP_VERSION__}`
    },
    signal
  };
  if (body !== undefined) {
    (options.headers as Record<string, string>)["Content-Type"] = "application/json";
    options.body = JSON.stringify(body);
  }
  const response = await fetch(path, options);
  onHeaders?.(response);
  if (!response.ok) {
    let data: unknown = null;
    try {
      data = await response.json();
    } catch {
      /* non-JSON response */
    }
    const message = data && typeof data === "object" && "error" in data
      ? String((data as { error?: unknown }).error)
      : `Request failed (${response.status}).`;
    throw new ApiError(message, response.status, data);
  }

  const reader = response.body?.getReader();
  if (!reader) throw new ApiError("Streaming is not supported by this browser.", 0, null);
  const decoder = new TextDecoder();
  let buffer = "";
  let eventName = "message";
  let dataLines: string[] = [];

  const dispatch = () => {
    if (!dataLines.length) {
      eventName = "message";
      return;
    }
    const raw = dataLines.join("\n");
    let data: unknown = raw;
    try {
      data = JSON.parse(raw);
    } catch {
      /* allow plain text payloads */
    }
    onEvent?.(eventName, data);
    eventName = "message";
    dataLines = [];
  };

  const readLine = (line: string) => {
    if (line === "") {
      dispatch();
      return;
    }
    if (line.startsWith(":")) return;
    const colon = line.indexOf(":");
    const field = colon === -1 ? line : line.slice(0, colon);
    const value = colon === -1 ? "" : line.slice(colon + 1).replace(/^ /, "");
    if (field === "event") eventName = value || "message";
    if (field === "data") dataLines.push(value);
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split(/\r?\n/);
    buffer = lines.pop() || "";
    for (const line of lines) readLine(line);
  }
  buffer += decoder.decode();
  if (buffer) {
    for (const line of buffer.split(/\r?\n/)) readLine(line);
  }
  dispatch();
}

function richNoteExportPath(
  id: string,
  options: RichNoteExportOptions = {},
  download = false
) {
  const query = new URLSearchParams({
    format: options.format || "markdown"
  });
  if (options.versionId) query.set("versionId", options.versionId);
  if (download) query.set("download", "1");
  return `/api/notes/${encodeURIComponent(id)}/content/export?${query.toString()}`;
}

/**
 * Server-streamed, authenticated export URL. It intentionally avoids browser
 * object URLs and direct storage URLs; Content-Disposition supplies the safe
 * filename.
 */
export function richNoteExportDownloadUrl(
  id: string,
  options: RichNoteExportOptions = {}
) {
  return richNoteExportPath(id, options, true);
}

export const api = {
  listSources: () => request<{ sources: SourceDocument[]; maxBytes: number }>("GET", "/api/sources"),
  uploadSourceFile,
  getSource: (id: string, page = 0) => request<{ source: SourceDocument; chunks: SourceChunk[] }>("GET", `/api/sources/${encodeURIComponent(id)}${page ? `?page=${page}` : ""}`),
  updateSource: (id: string, description: string) => request<{ source: SourceDocument }>("PATCH", `/api/sources/${encodeURIComponent(id)}`, { description }),
  listSourceChunks: (id: string, offset: number, page = 0) => request<{ chunks: SourceChunk[] }>("GET", `/api/sources/${encodeURIComponent(id)}/chunks?offset=${offset}${page ? `&page=${page}` : ""}`),
  searchSources: (query: string, sourceId = "") => request<{ results: SourceChunk[] }>("GET", `/api/sources/search?q=${encodeURIComponent(query)}${sourceId ? `&sourceId=${encodeURIComponent(sourceId)}` : ""}`),
  sourceDownloadUrl: (id: string) => `/api/sources/${encodeURIComponent(id)}/download`,
  retrySource: (id: string) => request<{ job: ChatJob }>("POST", `/api/sources/${encodeURIComponent(id)}/retry`, {}),
  deleteSource: (id: string) => request<{ deleted: boolean }>("DELETE", `/api/sources/${encodeURIComponent(id)}`),
  authStatus: () => request<AuthStatus>("GET", "/api/auth/status"),
  getRichContentCapabilities: (noteId = "") =>
    request<{
      capabilities: RichContentCapabilities;
      workspaceMetadataMutations?: {
        enabled: boolean;
        operations: Array<"folder.create" | "folder.update" | "folder.move" | "folder.trash" | "note.create" | "note.update" | "note.move" | "note.trash" | "trash.restore">;
      };
    }>(
      "GET",
      `/api/content/capabilities${noteId ? `?noteId=${encodeURIComponent(noteId)}` : ""}`
    ),
  login: (email: string, password: string, remember: boolean) =>
    request<{ user: User }>("POST", "/api/auth/login", { email, password, remember }),
  getInvite: (token: string) => request<{ invite: { email?: string; role?: string } }>("GET", `/api/auth/invite/${encodeURIComponent(token)}`),
  register: (token: string, email: string, password: string) =>
    request<{ user: User }>("POST", "/api/auth/register", { token, email, password }),
  requestPasswordReset: (email: string) =>
    request<{ ok?: boolean }>("POST", "/api/auth/password-reset/request", { email }),
  completePasswordReset: (token: string, password: string) =>
    request<{ ok?: boolean }>("POST", "/api/auth/password-reset/complete", { token, password }),
  logout: () => request<{ ok?: boolean }>("POST", "/api/auth/logout"),
  listUsers: () => request<{ users: User[] }>("GET", "/api/users"),
  listAccountErasures: () =>
    request<{ erasures: AccountErasureReceipt[] }>(
      "GET",
      "/api/account-erasures"
    ),
  eraseAccount: (
    userId: string,
    confirmation: string,
    operationId: string,
    exportAttestation = ""
  ) => request<{ erasure: AccountErasureReceipt }>(
    "POST",
    `/api/users/${encodeURIComponent(userId)}/erase`,
    {
      operationId,
      confirmation,
      exportAttestation: exportAttestation || undefined
    }
  ),
  resumeAccountErasure: (operationId: string) =>
    request<{ erasure: AccountErasureReceipt }>(
      "POST",
      `/api/account-erasures/${encodeURIComponent(operationId)}/resume`
    ),
  listInvites: () => request<{ invites: Invite[] }>("GET", "/api/invites"),
  createInvite: (email: string, role: string) =>
    request<{ token: string; link: string; delivery?: { delivered?: boolean; logged?: boolean; error?: string } | null }>("POST", "/api/invites", { email: email || undefined, role }),
  getRealtimeDiagnostics: () => request<RealtimeDiagnostics>("GET", "/api/admin/realtime"),
  listRecent: (limit = 24, after = "") => {
    const query = new URLSearchParams({ limit: String(limit) });
    if (after) query.set("after", after);
    return request<RecentResponse>("GET", `/api/recent?${query.toString()}`);
  },
  tree: () => request<TreeResponse>("GET", "/api/tree"),
  getRichNoteContent: (id: string) =>
    request<RichNoteContent>(
      "GET",
      `/api/notes/${encodeURIComponent(id)}/content`
    ),
  listRichNoteVersions: (id: string, limit = 50) =>
    request<RichNoteHistory>(
      "GET",
      `/api/notes/${encodeURIComponent(id)}/content/versions?limit=${encodeURIComponent(String(limit))}`
    ),
  getRichNoteVersion: (id: string, versionId: string) =>
    request<RichNoteSnapshot>(
      "GET",
      `/api/notes/${encodeURIComponent(id)}/content/versions/${encodeURIComponent(versionId)}`
    ),
  diffRichNoteVersion: (
    id: string,
    beforeVersionId: string,
    afterVersionId?: string
  ) => {
    const query = new URLSearchParams();
    if (afterVersionId) query.set("after", afterVersionId);
    const suffix = query.toString();
    return request<RichNoteHistoryDiff>(
      "GET",
      `/api/notes/${encodeURIComponent(id)}/content/versions/${encodeURIComponent(beforeVersionId)}/diff${suffix ? `?${suffix}` : ""}`
    );
  },
  restoreRichNoteVersion: (
    id: string,
    versionId: string,
    operationId: string
  ) => request<RichNoteHistoryRestore>(
    "POST",
    `/api/notes/${encodeURIComponent(id)}/content/versions/${encodeURIComponent(versionId)}/restore`,
    { operationId }
  ),
  exportRichNote: (
    id: string,
    options: RichNoteExportOptions = {}
  ) => request<RichNoteExport>(
    "GET",
    richNoteExportPath(id, options)
  ),
  richNoteExportDownloadUrl,
  richNoteAssetUrl,
  uploadRichNoteImage,
  getRichSyncBootstrap: (
    id: string,
    options: { afterSequence?: number; knownSnapshotId?: string } = {}
  ) => {
    const query = new URLSearchParams();
    if (options.afterSequence !== undefined) {
      query.set("after", String(options.afterSequence));
    }
    if (options.knownSnapshotId) {
      query.set("snapshot", options.knownSnapshotId);
    }
    const suffix = query.toString();
    return request<RichSyncBootstrap>(
      "GET",
      `/api/notes/${encodeURIComponent(id)}/content/sync${suffix ? `?${suffix}` : ""}`
    );
  },
  mintRichSyncToken: (
    id: string,
    body: {
      permission: "read" | "write";
      deviceId?: string;
      ttlSeconds?: number;
      clientCapabilities: {
        schema: string;
        schemaVersion: number;
        generation: string;
        schemaHash: string;
      };
    }
  ) =>
    request<{ sync: RichSyncToken }>(
      "POST",
      `/api/notes/${encodeURIComponent(id)}/content/sync-token`,
      body
    ),
  appendRichSyncUpdate: (id: string, body: RichSyncUpdateInput) =>
    request<{ receipt: RichSyncReceipt }>(
      "POST",
      `/api/notes/${encodeURIComponent(id)}/content/updates`,
      body
    ),
  getRichSyncReceipt: (id: string, updateId: string) =>
    request<{ receipt: RichSyncReceipt }>(
      "GET",
      `/api/notes/${encodeURIComponent(id)}/content/updates/${encodeURIComponent(updateId)}`
    ),
  search: (q: string) => request<SearchResponse>("GET", `/api/search?q=${encodeURIComponent(q)}`),
  searchRich: (
    q: string,
    options: RichSearchOptions = {}
  ) => {
    const query = new URLSearchParams({
      q,
      legacyFallback: options.legacyFallback || "none"
    });
    if (options.limit !== undefined) {
      query.set("limit", String(options.limit));
    }
    if (options.after) query.set("after", options.after);
    return request<RichSearchResponse>(
      "GET",
      `/api/search/rich?${query.toString()}`
    );
  },
  listRichSmartFolders: (
    options: { limit?: number; after?: string } = {}
  ) => {
    const query = new URLSearchParams();
    if (options.limit !== undefined) {
      query.set("limit", String(options.limit));
    }
    if (options.after) query.set("after", options.after);
    const suffix = query.toString();
    return request<RichSmartFolderListResponse>(
      "GET",
      `/api/smart-folders${suffix ? `?${suffix}` : ""}`
    );
  },
  listRichSmartFolderBuiltIns: () =>
    request<{
      format: "notey.rich-smart-folder-built-ins";
      contractVersion: 1;
      smartFolders: RichSmartFolder[];
    }>("GET", "/api/smart-folders/built-ins"),
  queryBuiltInRichSmartFolder: (
    id: string,
    options: { limit?: number; after?: string; timeZone?: string } = {}
  ) => {
    const query = new URLSearchParams();
    if (options.limit !== undefined) {
      query.set("limit", String(options.limit));
    }
    if (options.after) query.set("after", options.after);
    if (options.timeZone) query.set("timeZone", options.timeZone);
    const suffix = query.toString();
    return request<RichSmartFolderResultsResponse>(
      "GET",
      `/api/smart-folders/built-ins/${encodeURIComponent(id)}/results${suffix ? `?${suffix}` : ""}`
    );
  },
  previewRichSmartFolder: (
    body: Pick<RichSmartFolderCreateInput, "name" | "query" | "sort">,
    options: { limit?: number; timeZone?: string } = {}
  ) => {
    const query = new URLSearchParams();
    if (options.limit !== undefined) {
      query.set("limit", String(options.limit));
    }
    if (options.timeZone) query.set("timeZone", options.timeZone);
    const suffix = query.toString();
    return request<RichSmartFolderResultsResponse>(
      "POST",
      `/api/smart-folders/preview${suffix ? `?${suffix}` : ""}`,
      body
    );
  },
  getRichSmartFolder: (id: string) =>
    request<RichSmartFolderResponse>(
      "GET",
      `/api/smart-folders/${encodeURIComponent(id)}`
    ),
  createRichSmartFolder: (body: RichSmartFolderCreateInput) =>
    request<RichSmartFolderResponse>("POST", "/api/smart-folders", body),
  updateRichSmartFolder: (
    id: string,
    body: RichSmartFolderUpdateInput
  ) =>
    request<RichSmartFolderResponse>(
      "PATCH",
      `/api/smart-folders/${encodeURIComponent(id)}`,
      body
    ),
  deleteRichSmartFolder: (id: string, expectedRevision: number) =>
    request<{
      format: "notey.rich-smart-folder-deletion";
      contractVersion: 1;
      id: string;
      revision: number;
      deletedAt: string;
    }>(
      "DELETE",
      `/api/smart-folders/${encodeURIComponent(id)}?expectedRevision=${encodeURIComponent(String(expectedRevision))}`
    ),
  queryRichSmartFolder: (
    id: string,
    options: { limit?: number; after?: string; timeZone?: string } = {}
  ) => {
    const query = new URLSearchParams();
    if (options.limit !== undefined) {
      query.set("limit", String(options.limit));
    }
    if (options.after) query.set("after", options.after);
    if (options.timeZone) query.set("timeZone", options.timeZone);
    const suffix = query.toString();
    return request<RichSmartFolderResultsResponse>(
      "GET",
      `/api/smart-folders/${encodeURIComponent(id)}/results${suffix ? `?${suffix}` : ""}`
    );
  },
  semanticSearch: (q: string) =>
    request<SearchResponse & { error?: string }>(
      "GET",
      `/api/semantic?q=${encodeURIComponent(q)}`
    ),
  listModels: () => request<{ models: ModelInfo[]; freeRouterModel?: ModelInfo }>("GET", "/api/models"),
  listActions: (params: { view?: string; completed?: boolean; today?: string; noteId?: string; limit?: number } = {}) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== false && value !== "") {
        query.set(key, value === true ? "1" : String(value));
      }
    }
    const suffix = query.toString();
    return request<ActionsResponse>("GET", `/api/actions${suffix ? `?${suffix}` : ""}`);
  },
  updateAction: (
    id: string,
    body: {
      noteId?: string;
      idempotencyKey?: string;
      completed?: boolean;
      due?: string;
      reminder?: string;
    }
  ) =>
    request<{
      action?: ActionItem;
      note?: Note;
      mutationMode?: "rich-semantic" | "legacy-compatibility";
      receipt?: Record<string, unknown>;
    }>("PATCH", `/api/actions/${encodeURIComponent(id)}`, body),
  createSkill: (body: Record<string, unknown>) =>
    request<{ skill: Skill }>("POST", "/api/skills", body),
  updateSkill: (id: string, body: Record<string, unknown>) =>
    request<{ skill: Skill }>("PATCH", `/api/skills/${encodeURIComponent(id)}`, body),
  updateSkillMetadata: (id: string, body: Record<string, unknown>) =>
    request<{ skill: Skill }>("PATCH", `/api/skills/${encodeURIComponent(id)}/metadata`, body),
  updateSkillSchedule: (id: string, schedule: SkillSchedule) =>
    request<{ skill: Skill }>("PATCH", `/api/skills/${encodeURIComponent(id)}/schedule`, { schedule }),
  duplicateSkill: (id: string, body: { title?: string; remix?: boolean } = {}) =>
    request<{ skill: Skill }>("POST", `/api/skills/${encodeURIComponent(id)}/duplicate`, body),
  addSkillTest: (id: string, body: { input?: string; expect?: string }) =>
    request<{ skill: Skill }>("POST", `/api/skills/${encodeURIComponent(id)}/tests`, body),
  runSkillTest: (id: string, body: { input?: string; testId?: string }) =>
    request<{ job: ChatJob }>("POST", `/api/skills/${encodeURIComponent(id)}/tests/run`, body),
  recordSkillTestResult: (id: string, testId: string, body: { status: string; actual?: string; jobId?: string }) =>
    request<{ skill: Skill }>("POST", `/api/skills/${encodeURIComponent(id)}/tests/${encodeURIComponent(testId)}/result`, body),
  refineSkill: (id: string, body: { instruction?: string; observed?: string; expected?: string }) =>
    request<{ skill: Skill }>("POST", `/api/skills/${encodeURIComponent(id)}/refine`, body),
  refineSkillFromTest: (id: string, testId: string, body: { instruction?: string } = {}) =>
    request<{ skill: Skill }>("POST", `/api/skills/${encodeURIComponent(id)}/refine/tests/${encodeURIComponent(testId)}`, body),
  refineSkillFromRecentRun: (id: string, body: { jobId?: string; instruction?: string } = {}) =>
    request<{ skill: Skill }>("POST", `/api/skills/${encodeURIComponent(id)}/refine/recent-run`, body),
  listSkillVersions: (id: string, limit = 50) =>
    request<{ versions: SkillVersion[] }>("GET", `/api/skills/${encodeURIComponent(id)}/versions?limit=${encodeURIComponent(String(limit))}`),
  restoreSkillVersion: (id: string, versionId: string) =>
    request<{ skill: Skill; restoredFrom?: SkillVersion }>("POST", `/api/skills/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}/restore`, {
      operationId: `web-skill-rich-restore:${crypto.randomUUID()}`
    }),
  runSkill: (id: string, input: string) =>
    request<{ job: ChatJob }>("POST", `/api/skills/${encodeURIComponent(id)}/run`, { input }),
  exportSkill: (id: string) =>
    request<{ manifest: Record<string, unknown> }>("GET", `/api/skills/${encodeURIComponent(id)}/export`),
  importSkill: (manifest: Record<string, unknown>) =>
    request<{ skill: Skill }>("POST", "/api/skills/import", { manifest }),
  listSkills: (limit = 200) =>
    request<{ skills: Skill[]; sections?: SkillSections }>("GET", `/api/skills?limit=${encodeURIComponent(String(limit))}`),
  listStarterSkills: () =>
    request<{ starters: StarterSkill[] }>("GET", "/api/skills/starters"),
  installStarterSkill: (id: string, body: Record<string, unknown> = {}) =>
    request<{ skill: Skill }>("POST", `/api/skills/starters/${encodeURIComponent(id)}/install`, body),
  listWorkflows: (limit = 200) =>
    request<{ workflows: Workflow[] }>("GET", `/api/workflows?limit=${encodeURIComponent(String(limit))}`),
  listJobs: (limit = 50) =>
    request<{ jobs: ChatJob[] }>("GET", `/api/jobs?limit=${encodeURIComponent(String(limit))}`),
  listJobSummaries: (limit = 50) =>
    request<{ jobs: ChatJob[] }>("GET", `/api/jobs?view=summary&limit=${encodeURIComponent(String(limit))}`),
  getJob: (id: string) =>
    request<{ job: ChatJob }>("GET", `/api/jobs/${encodeURIComponent(id)}`),
  listPendingApprovals: () =>
    request<{ approvals: ChatApproval[] }>("GET", "/api/approvals/pending"),
  resolveApproval: (id: string, approved: boolean) =>
    request<{ ok?: boolean; approved?: boolean }>("POST", `/api/approvals/${encodeURIComponent(id)}/resolve`, { approved }),
  listChatThreads: (q = "", limit = 12, includeArchived = false) => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    params.set("limit", String(limit));
    if (includeArchived) params.set("archived", "1");
    return request<ChatThreadsResponse>("GET", `/api/chat/threads?${params.toString()}`);
  },
  dailyNote: () =>
    request<{ note: Note; created?: boolean }>("POST", "/api/daily-note", { date: new Date().toISOString().slice(0, 10) }),
  getNote: (id: string) =>
    request<{ note: Note; crdtSnapshot?: string | null }>("GET", `/api/notes/${encodeURIComponent(id)}`),
  recoverRichNoteCreate: (idempotencyKey: string) =>
    request<{
      recovery: {
        state: "committed" | "committed-note-unavailable" | "rich-commit-not-found";
        idempotencyKeyHash: string;
        note?: Note;
        noteId?: string | null;
        committedAt?: string | null;
      };
    }>("POST", "/api/notes/rich-create/recovery", { idempotencyKey }),
  createNote: (
    title = "Untitled",
    body = "",
    folderId?: string,
    options: {
      idempotencyKey?: string;
      tags?: string[];
      aiManaged?: boolean;
      pinned?: boolean;
    } = {}
  ) => request<{
    note: Note;
    createMode?: "rich";
    authority?: "rich-yjs";
    duplicate?: boolean;
  }>("POST", "/api/notes", { title, body, folderId, ...options }),
  createFolder: (
    parentId: string,
    name: string,
    guarded?: { idempotencyKey: string }
  ) => request<{ folder: FolderNode }>(
    "POST",
    "/api/folders",
    { parentId, name, ...guarded }
  ),
  updateFolder: (
    id: string,
    patch: { name: string } | { parentId: string },
    guarded?: { baseRev: number; idempotencyKey: string }
  ) => request<{ folder: FolderNode }>(
    "PATCH",
    `/api/folders/${encodeURIComponent(id)}`,
    { ...patch, ...guarded }
  ),
  deleteFolder: (
    id: string,
    guarded?: { baseRev: number; idempotencyKey: string }
  ) => request<{ deletedFolders?: number }>(
    "DELETE",
    `/api/folders/${encodeURIComponent(id)}`,
    guarded
  ),
  clip: (body: { url: string; folderId?: string }) =>
    request<{ note: Note }>("POST", "/api/clip", body),
  webPreview: (url: string) =>
    request<{ preview: WebPreview }>("POST", "/api/web-preview", { url }),
  transcribeAudio: (body: Record<string, unknown>, signal?: AbortSignal) =>
    request<{ text?: string; job?: ChatJob; model?: string; usage?: { cost?: number | null; seconds?: number | null } }>("POST", "/api/transcriptions", body, signal),
  listSpeechModels: () => request<{ models: SpeechModel[]; defaultModel: string; defaultVoice: string }>("GET", "/api/models/speech"),
  startVoiceSession: (threadId: string) => request<{ session: ChatJob }>("POST", "/api/voice/sessions", { threadId }),
  endVoiceSession: (id: string) => request<{ session: ChatJob }>("POST", `/api/voice/sessions/${encodeURIComponent(id)}/end`, {}),
  recordVoiceTiming: (id: string, body: { turnId: string; recognitionMs: number; chatFirstTextMs: number; modelFirstTextMs?: number; textBufferMs: number; chatToSpeechMs?: number; speechFirstAudioMs: number; totalFirstAudioMs: number; turnPauseMs: number; transcriptionModel: string; speechModel: string }) =>
    request<{ ok: boolean }>("POST", `/api/voice/sessions/${encodeURIComponent(id)}/timing`, body),
  prepareSpeech: (body: { input: string; model: string; voice: string; threadId?: string; voiceSessionId?: string }, signal?: AbortSignal) =>
    request<{ url: string; estimatedCost: number }>("POST", "/api/voice/speech/prepare", body, signal),
  openSpeechStream,
  synthesizeSpeech,
  prepareTranscription: (body: { name: string; mimeType: string; size: number; noteId?: string; contextNoteId?: string }) =>
    request<{ job: ChatJob }>("POST", "/api/transcriptions/prepare", body),
  updateTranscriptionUploadProgress: (id: string, progress: { phase: string; percent: number | null; message: string }) =>
    request<{ job: ChatJob }>("POST", `/api/transcriptions/${encodeURIComponent(id)}/progress`, progress),
  uploadTranscriptionFile,
  completeTranscriptionUpload: (id: string, body: { name: string; mimeType: string; size: number; noteId?: string; contextNoteId?: string; language?: string }) =>
    request<{ text?: string; job?: ChatJob }>("POST", `/api/transcriptions/${encodeURIComponent(id)}/complete`, body),
  createOcrNote: (body: Record<string, unknown>) =>
    request<{ note: Note }>("POST", "/api/ocr-notes", body),
  updateNote: (id: string, patch: Record<string, unknown>) =>
    request<{ note: Note }>("PUT", `/api/notes/${encodeURIComponent(id)}`, patch),
  moveNote: (
    id: string,
    fromRev: number,
    folderId: string,
    idempotencyKey: string
  ) => request<{ note: Note }>(
    "PUT",
    `/api/notes/${encodeURIComponent(id)}`,
    { folderId, baseRev: fromRev, idempotencyKey }
  ),
  updateNoteMetadata: async (
    id: string,
    expectedRevision: number,
    patch: NoteMetadataPatch
  ) => request<{ note: Note }>(
    "PUT",
    `/api/notes/${encodeURIComponent(id)}`,
    await buildNoteMetadataUpdate(id, expectedRevision, patch)
  ),
  syncNoteText: (id: string, text: string, title?: string) =>
    request<{ text: string; rev?: number }>("POST", `/api/notes/${encodeURIComponent(id)}/sync`, { text, title }),
  deleteNote: (
    id: string,
    guarded?: { baseRev: number; idempotencyKey: string }
  ) => request<{ ok?: boolean }>(
    "DELETE",
    `/api/notes/${encodeURIComponent(id)}`,
    guarded
  ),
  listTrash: () =>
    request<{ trash: { notes?: TrashItem[]; folders?: TrashItem[] } }>("GET", "/api/trash"),
  restoreTrash: (
    type: "note" | "folder",
    id: string,
    guarded?: { baseRev: number; idempotencyKey: string }
  ) => request<{
    note?: Note;
    folder?: FolderNode;
    restoredFolders?: number;
  }>(
    "POST",
    `/api/trash/${encodeURIComponent(type)}/${encodeURIComponent(id)}/restore`,
    guarded || {}
  ),
  emptyTrash: () =>
    request<WorkspacePurgeResult>("DELETE", "/api/trash"),
  backlinks: (id: string) =>
    request<BacklinksResponse>("GET", `/api/notes/${encodeURIComponent(id)}/backlinks`),
  outgoingLinks: (id: string) =>
    request<OutgoingLinksResponse>("GET", `/api/notes/${encodeURIComponent(id)}/links`),
  noteLinkSuggestions: (query: string, excludeNoteId?: string, limit = 12) => {
    const params = new URLSearchParams({
      q: query,
      limit: String(limit)
    });
    if (excludeNoteId) params.set("exclude", excludeNoteId);
    return request<NoteLinkSuggestionsResponse>(
      "GET",
      `/api/links/suggestions?${params}`
    );
  },
  related: (id: string) =>
    request<{
      authority?: "rich-document" | "legacy-markdown";
      projectionFresh?: boolean;
      stableBlockTargets?: boolean;
      fallbackReason?: string | null;
      related: NoteLinkPreview[];
    }>("GET", `/api/notes/${encodeURIComponent(id)}/related`),
  renamePropagate: (id: string, title: string) =>
    request<{ note: Note; updatedReferences?: Array<{ id: string; title?: string }> }>("POST", `/api/notes/${encodeURIComponent(id)}/rename-propagate`, { title }),
  listNoteVersions: (id: string) =>
    request<{ versions: NoteVersion[] }>("GET", `/api/notes/${encodeURIComponent(id)}/versions`),
  restoreNoteVersion: (id: string, versionId: string) =>
    request<{ note: Note; restoredFrom?: NoteVersion }>("POST", `/api/notes/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}/restore`, {}),
  listNoteShares: (id: string) =>
    request<{ shares: NoteShare[] }>("GET", `/api/notes/${encodeURIComponent(id)}/share`),
  createNoteShare: (id: string, allowPdf = false) =>
    request<{ share: NoteShare }>("POST", `/api/notes/${encodeURIComponent(id)}/share`, { allowPdf }),
  updateNoteShare: (id: string, allowPdf = false) =>
    request<{ share: NoteShare }>("PATCH", `/api/shares/${encodeURIComponent(id)}`, { allowPdf }),
  revokeNoteShare: (id: string) =>
    request<{ share: NoteShare }>("DELETE", `/api/shares/${encodeURIComponent(id)}`),
  createChatThread: (body: Record<string, unknown> = {}) =>
    request<ChatThreadDetail>("POST", "/api/chat/threads", body),
  getChatThread: (id: string, limit = 60, before = "") => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (before) params.set("before", before);
    return request<ChatThreadDetail>("GET", `/api/chat/threads/${encodeURIComponent(id)}?${params.toString()}`);
  },
  updateChatThread: (id: string, patch: Record<string, unknown>) =>
    request<{ thread?: ChatThread }>("PATCH", `/api/chat/threads/${encodeURIComponent(id)}`, patch),
  markChatThreadRead: (id: string) =>
    request<{ thread?: ChatThread }>("POST", `/api/chat/threads/${encodeURIComponent(id)}/read`, {}),
  markAllChatThreadsRead: () =>
    request<{ ok?: boolean; marked?: number; unreadTotal?: number }>("POST", "/api/chat/read-all", {}),
  heartbeatPresence: (body: { clientId: string; deviceLabel: string; surface: string; resourceId: string }) =>
    request<{ presence?: Record<string, unknown>; peers?: Array<Record<string, unknown>> }>("POST", "/api/presence", body),
  deleteChatThread: (id: string) =>
    request<{ ok?: boolean }>("DELETE", `/api/chat/threads/${encodeURIComponent(id)}`),
  cancelJob: (id: string) =>
    request<{ job?: ChatJob }>("POST", `/api/jobs/${encodeURIComponent(id)}/cancel`, {}),
  /**
   * Requests a server-side PDF render of the note. Returns immediately with
   * a queued "PDF export" job; the server renders it in the background
   * (headless Chrome) and the same job then completes (the rendered file is
   * re-downloadable via downloadPdfExport for the retention window).
   */
  requestPdfExport: (noteId: string) =>
    request<{ job: ChatJob }>("POST", `/api/notes/${encodeURIComponent(noteId)}/pdf-export`, {}),
  pdfExportDownloadUrl: (jobId: string) => `/api/jobs/${encodeURIComponent(jobId)}/pdf`,
  downloadPdfExport: (jobId: string, filename: string) =>
    downloadPdfExportFile(jobId, filename),
  sendChatThreadMessage: (id: string, content: string, body: Record<string, unknown> = {}) =>
    request<ChatSendResponse>("POST", `/api/chat/threads/${encodeURIComponent(id)}/messages`, { content, ...body }),
  streamChatThreadMessage: (
    id: string,
    content: string,
    body: Record<string, unknown> = {},
    options: { signal?: AbortSignal; onEvent?: (eventName: string, data: unknown) => void; onHeaders?: (response: Response) => void; limit?: number } = {}
  ) => {
    const params = new URLSearchParams();
    if (options.limit) params.set("limit", String(options.limit));
    const suffix = params.toString();
    return streamRequest(
      "POST",
      `/api/chat/threads/${encodeURIComponent(id)}/messages/stream${suffix ? `?${suffix}` : ""}`,
      { content, ...body },
      options
    );
  },
  getSettings: () => request<SettingsResponse>("GET", "/api/settings"),
  setOpenRouterKey: (key: string) =>
    request<{ ok?: boolean; hasKey?: boolean }>("POST", "/api/settings/openrouter-key", { key }),
  setSearchKeys: (body: { brave?: string; tavily?: string }) =>
    request<{ ok?: boolean; searchKeys?: SettingsResponse["settings"]["searchKeys"] }>("POST", "/api/settings/search-keys", body),
  setFreeMode: (enabled: boolean) =>
    request<{ ok?: boolean; freeModeEnabled?: boolean; freeRouter?: FreeRouterStatus }>("POST", "/api/settings/free-mode", { enabled }),
  setChatModel: (model: string) =>
    request<{ ok?: boolean; chatModel?: string | null }>("POST", "/api/settings/chat-model", { model }),
  setTranscriptionModel: (model: string) =>
    request<{ ok?: boolean; transcriptionModel?: string }>("POST", "/api/settings/transcription-model", { model }),
  setResearchModel: (model: string) =>
    request<{ ok?: boolean; researchModel?: string | null }>("POST", "/api/settings/research-model", { model }),
  setStartScreen: (startScreen: string) =>
    request<{ ok?: boolean; startScreen?: string }>("POST", "/api/settings/start-screen", { startScreen }),
  setMemoryPreferences: (memoryPreferences: { enabled: boolean; learningMode: "review" | "off"; episodeRetentionDays: number }) =>
    request<{ ok?: boolean; memoryPreferences?: SettingsResponse["settings"]["memoryPreferences"] }>("POST", "/api/settings/memory", memoryPreferences),
  completeOnboarding: () =>
    request<{ ok?: boolean; onboardingComplete?: boolean }>("POST", "/api/settings/onboarding-complete", {}),
  regenerateClipToken: () =>
    request<{ clipToken: string }>("POST", "/api/settings/clip-token", {}),
  listPushSubscriptions: () =>
    request<{ subscriptions: PushSubscriptionSummary[] }>("GET", "/api/push/subscriptions"),
  listTranscriptionModels: () =>
    request<{ models: ModelInfo[]; defaultModel?: string }>("GET", "/api/models/transcription"),
  listTranscriptionConnectors: () =>
    request<{ connectors: TranscriptionConnector[] }>("GET", "/api/transcription-connectors"),
  createTranscriptionConnector: (displayName: string) =>
    request<{ connector: TranscriptionConnector; token: string }>("POST", "/api/transcription-connectors", { displayName }),
  deleteTranscriptionConnector: (id: string) =>
    request<{ ok?: boolean; connector?: TranscriptionConnector }>("DELETE", `/api/transcription-connectors/${encodeURIComponent(id)}`),
  listWikiApps: () =>
    request<{ apps: WikiApp[] }>("GET", "/api/wiki-apps"),
  createWikiApp: (body: { displayName?: string; baseUrl?: string; bridgeToken?: string }) =>
    request<{ app: WikiApp }>("POST", "/api/wiki-apps", body),
  validateWikiApp: (id: string) =>
    request<{ app: WikiApp }>("POST", `/api/wiki-apps/${encodeURIComponent(id)}/validate`, {}),
  deleteWikiApp: (id: string) =>
    request<{ ok?: boolean }>("DELETE", `/api/wiki-apps/${encodeURIComponent(id)}`),
  listMcpServers: () =>
    request<{ servers: McpServer[] }>("GET", "/api/mcp/servers"),
  listMcpPresets: () =>
    request<{ presets: McpPreset[] }>("GET", "/api/mcp/presets"),
  setupMcpPreset: (id: string, body: { baseUrl?: string } = {}) =>
    request<{ server: McpServer }>("POST", `/api/mcp/presets/${encodeURIComponent(id)}/setup`, body),
  startMcpOAuth: (id: string) =>
    request<{ authorizationUrl?: string; url?: string; server?: McpServer }>("POST", `/api/mcp/servers/${encodeURIComponent(id)}/oauth/start`, {}),
  listMemory: (params: { candidates?: boolean; type?: string; scope?: string; disabled?: boolean; archived?: boolean; limit?: number } = {}) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== false && value !== "") {
        query.set(key, value === true ? "1" : String(value));
      }
    }
    const suffix = query.toString();
    return request<{ memories: AiMemory[] }>("GET", `/api/memory${suffix ? `?${suffix}` : ""}`);
  },
  getMemoryDiagnostics: () => request<{ diagnostics: { total: number; active: number; candidates: number; archived: number; expired: number; projectionFresh: number | null; embeddingIndexed: number | null } }>("GET", "/api/memory/diagnostics"),
  listMemoryEvidence: (id: string, limit = 100) =>
    request<{ evidence: AiMemoryEvidence[] }>("GET", `/api/memory/${encodeURIComponent(id)}/evidence?limit=${encodeURIComponent(limit)}`),
  listMemoryEpisodes: (params: { threadId?: string; limit?: number } = {}) => {
    const query = new URLSearchParams();
    if (params.threadId) query.set("threadId", params.threadId);
    if (params.limit) query.set("limit", String(params.limit));
    const suffix = query.toString();
    return request<{ episodes: AiMemoryEpisode[] }>("GET", `/api/memory/episodes${suffix ? `?${suffix}` : ""}`);
  },
  createMemory: (body: Record<string, unknown>) =>
    request<{ memory: AiMemory }>("POST", "/api/memory", body),
  updateMemory: (id: string, body: Record<string, unknown>) =>
    request<{ memory: AiMemory }>("PATCH", `/api/memory/${encodeURIComponent(id)}`, body),
  archiveMemory: (id: string) =>
    request<{ memory: AiMemory }>("DELETE", `/api/memory/${encodeURIComponent(id)}`),
  forgetMemory: (id: string) =>
    request<{ result: { operationId: string; state: string } }>("DELETE", `/api/memory/${encodeURIComponent(id)}/forget`),
  acceptMemoryCandidate: (id: string) =>
    request<{ memory: AiMemory }>("POST", `/api/memory/candidates/${encodeURIComponent(id)}/accept`, {}),
  rejectMemoryCandidate: (id: string) =>
    request<{ candidate: AiMemory }>("POST", `/api/memory/candidates/${encodeURIComponent(id)}/reject`, {}),
  listAiLogs: (kind = "", limit: number | "" = "") => {
    const query = new URLSearchParams();
    if (kind) query.set("kind", kind);
    if (limit) query.set("limit", String(limit));
    const suffix = query.toString();
    return request<{ logs: AiLogSummary[] }>("GET", `/api/logs/ai${suffix ? `?${suffix}` : ""}`);
  },
  getAiLog: (id: string) =>
    request<{ log: AiLog }>("GET", `/api/logs/ai/${encodeURIComponent(id)}`),
  getVapidPublicKey: () => request<{ key: string }>("GET", "/api/push/vapid-public-key"),
  subscribePush: (subscription: PushSubscriptionJSON) =>
    request<{ ok?: boolean }>("POST", "/api/push/subscriptions", subscription),
  unsubscribePush: (endpoint: string) =>
    request<{ ok?: boolean }>("POST", "/api/push/unsubscribe", { endpoint }),
  testPush: () => request<{ ok?: boolean }>("POST", "/api/push/test", {})
};
